import React, { useState, useEffect } from "react";
import "../styles/GestionOffre.css";
import AuthentifiedPage from "./AuthentifiedPage";
import { useAuth } from "../context/AuthContext";
import { OffreService } from "../services/OffreService";
import { projectService } from "../services/ProjetService";
import { AO, AOPrestataire, Prestataire } from "../types/AO";
import { Project } from "../types/Project";
import CollecteSuccessMessage from "../components/CollecteSuccessMessage";
import CollecteErrorMessage from "../components/CollecteErrorMessage";
import CollecteLoadingMessage from "../components/CollecteLoadingMessage";
import { IconType } from "react-icons";
import { FaDownload } from "react-icons/fa";
import { ENV } from "../constants/env";

const ALLOWED_STATUSES = ["valider", "rejeter", "resultat_de_marche", "cloture", "evaluation"];

const GestionOffre: React.FC = () => {
  const { api } = useAuth();

  const [offres, setOffres] = useState<AO[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showProjectInput, setShowProjectInput] = useState(false);
  const [projectStartDate, setProjectStartDate] = useState<string>(
    new Date().toISOString().slice(0,16)
  );
  const [projectEndDate, setProjectEndDate] = useState<string>(
    new Date(Date.now() + 7*24*60*60*1000).toISOString().slice(0,16)
  );
  const [projectName, setProjectName] = useState("");
  const [step, setStep] = useState<1 | 2>(1);
  const [tempAO, setTempAO] = useState<AO>({
    idAo: 0,
    reference: "",
    titre: "",
    description: "",
    datePublication: new Date().toISOString().split("T")[0],
    dateCloturation: "",
    path: "",
    statut: "en_cours",
    exigence: [],
    daoFile: null,
  });

  const [documents, setDocuments] = useState<string[]>([]);
  const [allPrestataires, setAllPrestataires] = useState<Prestataire[]>([]);
  const [prestatairesSelection, setPrestatairesSelection] = useState<Prestataire[]>([]);
  const [currentAOId, setCurrentAOId] = useState<number | null>(null);
  const [showPrestataireModal, setShowPrestataireModal] = useState(false);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [evaluationNotes, setEvaluationNotes] = useState<{ [k: string]: string }>({});
  const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
  const [selectedPrestataireId, setSelectedPrestataireId] = useState<number | null>(null);

  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  const [loadingVisible, setLoadingVisible] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState<Prestataire | null>(null);
  const [documentsText, setDocumentsText] = useState("");

  const [searchPrestataire, setSearchPrestataire] = useState("");
  const [filteredPrestataires, setFilteredPrestataires] = useState<Prestataire[]>([]);
  const [suggestionSelected, setSuggestionSelected] = useState<Prestataire | null>(null);

  const currentDate = new Date();
  const isOverdue = tempAO.dateCloturation ? new Date(tempAO.dateCloturation) < currentDate : false;

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const statusClassMap: { [key: string]: string } = {
    valider: "status-valider",
    rejeter: "status-rejeter",
    resultat_de_marche: "status-resultat_de_marche",
    cloture: "status-cloture",
    publier: "status-publie",
    en_cours: "status-en_cours",
  };


  const SafeIcon: React.FC<{ icon?: IconType; size?: number; className?: string }> = ({ icon, size, className }) => {
    if (!icon) return null;
    const IconComp = icon as React.ElementType;
    return <IconComp size={size} className={className} />;
  };

  const [currentAOStatus, setCurrentAOStatus] = useState<string>("");

  useEffect(() => {
    loadAO();
    loadPrestataires();
  }, []);

    const loadAO = async () => {
      try {
        const data = await OffreService.getAllAO(api);
        
        const now = new Date();
        for (const ao of data) {
          if (ao.statut === "en_cours" && ao.dateCloturation && new Date(ao.dateCloturation) < now) {
            await changeAOStatus(ao.idAo!, "cloture");
            ao.statut = "cloture";
          }
        }

        setOffres(data);
      } catch (e) {
        console.error(e);
      }
    };


  const loadPrestataires = async () => {
    try {
      const data = await OffreService.getAllPrestataires(api);
      setAllPrestataires(data);
    } catch (e) { console.error(e); }
  };  

  const createNewProject = async (project: Project) => {
    try {
      const exists = await projectService.isAlreadyAProject(api, project);

      if (exists) {
        alert("Un projet existe déjà pour cet appel d'offre !");
        return;
      }

      const message = await projectService.createProject(api, project);
      alert(message);

    } catch (e) {
      console.error("Erreur lors de la création du projet :", e);
      alert("Erreur lors de la création du projet !");
    }
  };

  useEffect(() => {
  if (searchPrestataire.trim() === "") {
    setFilteredPrestataires([]);
    setSuggestionSelected(null);
    return;
  }

  const q = searchPrestataire.toLowerCase();

  setFilteredPrestataires(
    allPrestataires.filter(p =>
      (`${p.firstName} ${p.lastName} ${p.entrepriseName}`)
        .toLowerCase()
        .includes(q)
    )
  );
}, [searchPrestataire, allPrestataires]);

  // ------------------- Formulaire AO -------------------
  const handleChangeAO = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTempAO({ ...tempAO, [name]: value });
  };

  const getCountdown = (dateStr: string | null) => {
    if (!dateStr) return { text: "-", className: "" };

    const now = new Date();
    const targetDate = new Date(dateStr);
    const diffMs = targetDate.getTime() - now.getTime();
    const absDiffDays = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(absDiffDays / 7);
    const diffMonths = Math.floor(absDiffDays / 30);

    let text = "";
    let className = "";

    if (diffMs > 0) { 
      if (diffMonths >= 1) {
        text = `Échéance dans ${diffMonths} mois`;
        className = "countdown-month";
      } else if (diffWeeks >= 1) {
        text = `Échéance dans ${diffWeeks} semaine${diffWeeks > 1 ? 's' : ''}`;
        className = "countdown-week";
      } else {
        text = `Échéance dans ${absDiffDays} jour${absDiffDays > 1 ? 's' : ''}`;
        className = "countdown-day";
      }
    } else { 
      if (diffMonths >= 1) {
        text = `Clôturé il y a ${diffMonths} mois`;
        className = "countdown-month";
      } else if (diffWeeks >= 1) {
        text = `Clôturé il y a ${diffWeeks} semaine${diffWeeks > 1 ? 's' : ''}`;
        className = "countdown-week";
      } else {
        text = `Clôturé il y a ${absDiffDays} jour${absDiffDays > 1 ? 's' : ''}`;
        className = "countdown-day";
      }
    }

    return { text, className };
  };

  const validateEvaluation = async () => {
    if (!selectedPrestataireId || !currentAOId) return;

    try {
      setLoadingVisible(true);
      setLoadingMessage("Validation de l'évaluation...");

      const associatedList: AOPrestataire[] = await OffreService.getByIdAO(api, currentAOId);
      const selectedAOPrestataire = associatedList.find(
        aop => aop.idPrestataire === selectedPrestataireId
      );

      if (selectedAOPrestataire && selectedAOPrestataire.idAoPrestataire) {
        await OffreService.enableStatus(api, selectedAOPrestataire.idAoPrestataire);

        setPrestatairesSelection(prev =>
          prev.map(p =>
            p.idPrestataire === selectedPrestataireId ? { ...p, isAccepted: true } : p
          )
        );
        await changeAOStatus(currentAOId, "resultat_de_marche");
      }

      setShowEvaluationModal(false);
      setLoadingVisible(false);
      setSuccessMessage("Prestataire validé avec succès !");
    } catch (error) {
      setLoadingVisible(false);
      setErrorMessage("Erreur lors de la validation du prestataire !");
      console.error("Erreur lors de l'acceptation du prestataire :", error);
    }
  };

  const handleDaoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];

    setTempAO({
      ...tempAO,
      daoFile: file,
      path: file.name,
    });

  };

  const nextStep = () => setStep(2);
  const handleAddDocument = async () => {
    const finalAO: AO = { 
      ...tempAO, 
      exigence: documentsText
        .split(",")               
        .map(d => d.trim())     
        .filter(d => d)          
    };

    try {
      setLoadingVisible(true);
      setLoadingMessage("Création de l'appel d'offre...");

      const created = await OffreService.createAO(api, finalAO);

      setOffres([...offres, created]);
      setLoadingVisible(false);
      setSuccessMessage("Appel d'offre créé avec succès !");

      resetModal();
    } catch (error) {
      setLoadingVisible(false);
      setErrorMessage("Erreur lors de la création de l'appel d'offre !");
      console.error(error);
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setStep(1);
    setTempAO({
      idAo: 0,
      reference: "",
      titre: "",
      description: "",
      datePublication: new Date().toISOString().split("T")[0],
      dateCloturation: "",
      path: "",
      statut: "en_cours",
      exigence: []
    });
    setDocuments([]);
  };

  // ------------------- Prestataires / Évaluation / Détails -------------------
  const openPrestataire = async (ao: AO) => {
    setCurrentAOId(ao.idAo!);
    setPrestatairesSelection([]);
    setCurrentAOStatus(ao.statut);
    setActionMenuOpen(null);

    try {
      const all = await OffreService.getAllPrestataires(api);
      setAllPrestataires(all);

      const associated: AOPrestataire[] = await OffreService.getByIdAO(api, ao.idAo!);
      const selected = all.filter(p => associated.some(a => a.idPrestataire === p.idPrestataire));
      setPrestatairesSelection(selected);

      setShowPrestataireModal(true);
    } catch (e) { console.error(e); }
  };

  const openEvaluation = async (ao: AO) => {
    setCurrentAOId(ao.idAo!);
    setCurrentAOStatus(ao.statut);
    setActionMenuOpen(null);

    try {
      const associated: AOPrestataire[] = await OffreService.getByIdAO(api, ao.idAo!);
      const selected = allPrestataires.filter(p => associated.some(a => a.idPrestataire === p.idPrestataire));
      setPrestatairesSelection(selected);

      setEvaluationNotes(selected.reduce((acc, p) => ({ ...acc, [p.firstName]: "" }), {}));
      setShowEvaluationModal(true);
    } catch (e) { console.error(e); }
  };

  const togglePrestataire = (p: Prestataire) => {
    if (tempAO.statut === "publier") return; 
    setPrestatairesSelection(prev =>
      prev.some(x => x.idPrestataire === p.idPrestataire)
        ? prev.filter(x => x.idPrestataire !== p.idPrestataire)
        : [...prev, p]
    );
  };

  const savePrestataires = async () => {
    if (!currentAOId) return;

    try {
      setLoadingVisible(true);
      setLoadingMessage("Enregistrement des prestataires...");

      const list: AOPrestataire[] = prestatairesSelection
        .filter(p => p.idPrestataire !== undefined)
        .map(p => ({ idAo: currentAOId, idPrestataire: p.idPrestataire!, status: "evaluation" }));

      await OffreService.createAOPrestataires(api, list);

      const currentDate = new Date();
      const ao = await OffreService.getAOById(api, currentAOId);
      const echeanceDate = new Date(ao.dateCloturation!);

      if (echeanceDate < currentDate) {
        await changeAOStatus(currentAOId, "cloture");
      } else {
        await changeAOStatus(currentAOId, "en cours");
      }

      setLoadingVisible(false);
      setSuccessMessage("Prestataires enregistrés avec succès !");
      setShowPrestataireModal(false);

    } catch (error) {
      setLoadingVisible(false);
      setErrorMessage("Erreur lors de l'enregistrement des prestataires !");
      console.error(error);
    }
  };


  const changeAOStatus = async (aoId: number, status: string) => {
    if (!ALLOWED_STATUSES.includes(status)) { console.warn("Statut invalide :", status); return; }
    try { await OffreService.changeAOStatus(api, aoId, status); loadAO(); } catch (e) { console.error(e); }
  };

  const finalizeEvaluation = (accepted: boolean) => {
    if (!currentAOId) return;
    changeAOStatus(currentAOId, accepted ? "resultat_de_marche" : "cloture");
    setShowEvaluationModal(false);
  };

  const openDetails = async (ao: AO) => {
    setTempAO(ao);
    setShowDetailsModal(true);
    setActionMenuOpen(null);

    try {
      const aopList: AOPrestataire[] = await OffreService.getByIdAO(api, ao.idAo!);
      const winnerLink = aopList.find(a => a.isAccepted === true);

      if (winnerLink) {
        const winner = allPrestataires.find(p => p.idPrestataire === winnerLink.idPrestataire);
        setSelectedWinner(winner || null);
      } else {
        setSelectedWinner(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [showPrestataireAddModal, setShowPrestataireAddModal] = useState(false);

  const [tempPrestataire, setTempPrestataire] = useState<Prestataire>({
    firstName: "",
    lastName: "",
    entrepriseName: "",
    adresse: "",
    email: "",
    telephone: "",
  });

  const handleChangePrestataire = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTempPrestataire({ ...tempPrestataire, [name]: value });
  };

  const handleAddPrestataire = async () => {
    if (
      !tempPrestataire.firstName.trim() ||
      !tempPrestataire.lastName.trim() ||
      !tempPrestataire.entrepriseName.trim() ||
      !tempPrestataire.adresse.trim() ||
      !tempPrestataire.email.trim() ||
      !tempPrestataire.telephone.trim()
    ) {
      setErrorMessage("Veuillez remplir tous les champs avant de créer le prestataire.");
      return;
    }

    try {
      setLoadingVisible(true);
      setLoadingMessage("Création du prestataire...");

      const created = await OffreService.createPrestataire(api, tempPrestataire);

      await loadPrestataires();

      setSuccessMessage("Prestataire créé avec succès !");
      setShowPrestataireAddModal(false);

      setTempPrestataire({
        firstName: "",
        lastName: "",
        entrepriseName: "",
        adresse: "",
        email: "",
        telephone: "",
      });

    } catch (error: any) {
      console.error("Erreur lors de la création du prestataire :", error);

      if (error.message === "EMAIL_TAKEN") {
        setErrorMessage("Cet email est déjà utilisé.");
      } else if (error.message === "SERVER_ERROR") {
        setErrorMessage("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setErrorMessage("Erreur lors de la création du prestataire.");
      }

    } finally {
      setLoadingVisible(false);
    }
  };

  const handlePublishOffer = async () => {
    if (!tempAO.idAo) return;

    try {
      setLoadingVisible(true);
      setLoadingMessage("Publication de l'offre...");

      const publishedAO = await OffreService.publishAO(api, tempAO.idAo);

      setOffres(prev =>
        prev.map(o => (o.idAo === publishedAO.idAo ? publishedAO : o))
      );

      setTempAO(publishedAO);
      setLoadingVisible(false);
      setSuccessMessage("Offre publiée avec succès !");
      setShowDetailsModal(false);

    } catch (error) {
      setLoadingVisible(false);
      setErrorMessage("Erreur lors de la publication de l'offre !");
      console.error(error);
    }
  };

  const changeToProject = (idAo?: number) => {
    if (idAo === undefined) {
      alert("ID AO non défini !");
      return;
    }
    setShowProjectInput(true);
  };

  const closeOnOverlay = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, setShow: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (e.target === e.currentTarget) setShow(false);
  };

  const displayStatus = (statut: string) => {
    if (statut === "resultat_de_marche") return "Attribué";
    return statut.replaceAll("_", " ");
  };

  const filteredOffres = offres.filter(o => {
    const q = query.toLowerCase();
    const matchesQuery =
      o.reference.toLowerCase().includes(q) ||
      o.titre.toLowerCase().includes(q) ||
      o.description.toLowerCase().includes(q);
    const matchesStatus = statusFilter ? o.statut === statusFilter : true;
    return matchesQuery && matchesStatus;
  });

  const [page, setPage] = useState(1);
  const pageSize = 10; 
  const total = filteredOffres.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pageData = filteredOffres.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter]);

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <AuthentifiedPage>

      <CollecteSuccessMessage
      message={successMessage}
      visible={!!successMessage}
      onClose={() => setSuccessMessage("")}
    />

    <CollecteErrorMessage
      message={errorMessage}
      visible={!!errorMessage}
      onClose={() => setErrorMessage("")}
    />

    <CollecteLoadingMessage
      message={loadingMessage}
      visible={loadingVisible}
    />

      <div className="container mt-3">
        <div className="d-flex justify-content-between">
          <h4 className="text-success fw-bold">Gestion des Appels d’Offres</h4>
            <div className="d-flex justify-content-between mb-3">
              <div className="btn-add-container">
                <button className="btn-add-offre" onClick={() => setShowModal(true)}>
                  + Créer une offre
                </button>
              </div>
            </div>
        </div>

        <div className="gestion-offre-filters mt-3">
          <div className="input-wrapper">
            <span className="search-icon">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher référence / titre / description"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <select
            className="custom-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            {ALLOWED_STATUSES.map(s => (
              <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
            ))}
          </select>
        </div>

        <table className="table table-hover mt-3">
          <thead className="table-success">
            <tr>
              <th>Référence</th>
              <th>Titre</th>
              <th>Délai</th>
              <th>DAO</th>
              <th>Statut</th>
              <th></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
              {pageData.map(o => {
              const isOverdue = o.dateCloturation && new Date(o.dateCloturation) < new Date();
              let statusDisplay = o.statut;
              if (statusDisplay === "en_cours" && isOverdue) statusDisplay = "cloture";
                return (
                  <tr key={o.idAo}>
                    <td>{o.reference}</td>
                    <td>{o.titre}</td>  
                    <td>
                      {(() => {
                        const countdown = getCountdown(o.dateCloturation);
                        return <span className={countdown.className}>{countdown.text}</span>;
                      })()}
                    </td>
                    <td>
                      {o.path ? (
                        <a
                          href={`${ENV.API_URL}/ao/files/${o.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-download"
                        >
                          <SafeIcon icon={FaDownload} size={16} />
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <span className={`status ${statusClassMap[statusDisplay] || ""}`}>
                        {displayStatus(statusDisplay)}
                      </span>
                    </td>
                    <td>
                    <span className={`status ${o.isPublished ? 'status-publie' : 'status-non-publie'}`}>
                      {o.isPublished ? 'Publié' : 'Non publié'}
                    </span>
                  </td>
                    <td className="position-relative">
                      <button className="btn-icon" onClick={() => setActionMenuOpen(actionMenuOpen === o.idAo ? null : o.idAo!)}>&#x22EE;</button>
                      {actionMenuOpen === o.idAo && (
                        <div className="action-menu">
                            <>
                          <button onClick={() => openPrestataire(o)}>Assigner un préstataire</button>
                          <button
                            onClick={() => openEvaluation(o)}
                            disabled={
                              prestatairesSelection.length === 0 || 
                              o.statut === "resultat_de_marche" 
                            }
                          >
                            Faire une Évaluation
                            </button>                            
                          </> 
                          <button onClick={() => openDetails(o)}>Détails</button>
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
          </tbody>
        </table>
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <small className="text-muted">
            {pageData.length} offres affichées sur {total}
          </small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {pageNumbers.map(num => (
                <li key={num} className={`page-item ${num === currentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Modal Création AO */}
        {showModal && (
          <div className="modal-overlay" onClick={e => closeOnOverlay(e, setShowModal)}>
            <div className="modal-box">
              {step === 1 ? (
                <>
                  <h3>Ajouter un appel d’offre</h3>
                  <input name="reference" placeholder="Référence" className="form-control mb-2" value={tempAO.reference} onChange={handleChangeAO} />
                  <input name="titre" placeholder="Titre" className="form-control mb-2" value={tempAO.titre} onChange={handleChangeAO} />
                  <textarea name="description" placeholder="Description" className="form-control mb-2" value={tempAO.description} onChange={handleChangeAO} />
                  <input type="date" name="dateCloturation" className="form-control mb-2" value={tempAO.dateCloturation || ""} onChange={handleChangeAO} />
                  <label className="fw-bold">Document DAO (PDF)</label>
                  <input type="file" accept="application/pdf" className="form-control mb-3" onChange={handleDaoUpload} />
                  <div className="text-end">
                    <button className="btn btn-secondary me-2" onClick={resetModal}>Annuler</button>
                    <button className="btn btn-success" onClick={nextStep}>Suivant</button>
                  </div>
                </>
              ) : (
                <>
                  <h3>Exigences/Documents requis</h3>
                    <textarea
                      rows={4}
                      className="form-control mb-3"
                      placeholder="Séparer par des virgules"
                      value={documentsText}
                      onChange={e => setDocumentsText(e.target.value)}
                    />                  
                    <div className="text-end">
                    <button className="btn btn-secondary me-2" onClick={resetModal}>Annuler</button>
                    <button className="btn btn-success" onClick={handleAddDocument}>Enregistrer</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Modal Prestataires */}
        {showPrestataireModal && (
          <div className="modal-overlay" onClick={e => closeOnOverlay(e, setShowPrestataireModal)}>
            <div className="modal-box">
              <h4 className="mt-3">Prestataires sélectionnés</h4>

              {prestatairesSelection.length === 0 && (
                <p className="text-muted">Aucun prestataire sélectionné</p>
              )}

              {prestatairesSelection.map(p => (
                <div key={p.idPrestataire} className="form-check mb-1">
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => togglePrestataire(p)}
                    disabled={currentAOStatus !== "en_cours"} 
                  />
                  <label className="ms-2">
                    {p.firstName} {p.lastName} — {p.entrepriseName}
                  </label>
                </div>
              ))}

              {/* Barre de recherche + bouton + */}
              <div className="search-add-container mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un prestataire..."
                  value={searchPrestataire}
                  onChange={e => setSearchPrestataire(e.target.value)}
                  disabled={currentAOStatus !== "en_cours"} 
                />

                <button
                  className="btn-add-offre ms-2"
                  onClick={() => setShowPrestataireAddModal(true)}
                  disabled={currentAOStatus !== "en_cours"}
                >
                  +
                </button>
              </div>

              {/* Suggestions dynamiques */}
              {filteredPrestataires.length > 0 && (
                <div className="suggestion-box">
                  {filteredPrestataires.map(p => (
                    <div
                      key={p.idPrestataire}
                      className="suggestion-item"
                      onClick={() => {
                        if (currentAOStatus !== "en_cours") return;

                        setSearchPrestataire("");

                        if (!prestatairesSelection.some(x => x.idPrestataire === p.idPrestataire)) {
                          setPrestatairesSelection(prev => [...prev, p]);
                        }

                        setFilteredPrestataires([]);
                      }}
                    >
                      {p.firstName} {p.lastName} — {p.entrepriseName}
                    </div>
                  ))}
                </div>
              )}

              {/* Boutons Valider / Annuler */}
              {(currentAOStatus !== "cloture" && currentAOStatus !== "resultat_de_marche") && (
                <div className="text-end mt-3">
                  <button className="btn btn-secondary me-2" onClick={() => setShowPrestataireModal(false)}>Annuler</button>
                  <button className="btn btn-success" onClick={savePrestataires}>Valider</button>
                </div>
              )}
            </div>
          </div>
        )}


      {/* Modal Évaluation */}
      {showEvaluationModal && (
        <div className="modal-overlay" onClick={e => closeOnOverlay(e, setShowEvaluationModal)}>
          <div className="modal-box">
            <h3>Évaluation des Prestataires</h3>
            {prestatairesSelection.map(p => (
              <div key={p.idPrestataire} className="form-check mb-2">
                <input
                  type="radio"
                  name={`prestataire-${p.idPrestataire}`}
                  value={p.idPrestataire}
                  checked={selectedPrestataireId === p.idPrestataire}
                  onChange={() => setSelectedPrestataireId(p.idPrestataire!)}
                  disabled={currentAOStatus === "resultat_de_marche"} 
                />
                <label htmlFor={`eval-${p.idPrestataire}`} className="ms-2">
                  {p.firstName} {p.lastName}
                </label>
              </div>
            ))}
              <div className="text-end mt-3">
                { currentAOStatus !== "resultat_de_marche" && (
                  <>
                    <button className="btn btn-secondary me-2" onClick={() => setShowEvaluationModal(false)}>Annuler</button>
                    <button className="btn btn-success" onClick={validateEvaluation}>
                      Valider
                    </button>
                    <button
                      className="btn btn-danger ms-2"
                      onClick={() => { changeAOStatus(currentAOId!, "rejeter"); setShowEvaluationModal(false); }}
                    >
                      Rejeter
                    </button>
                  </>
                )}
              </div>

          </div>
        </div>
      )}
      {showPrestataireAddModal && (
        <div className="modal-overlay" onClick={e => { if(e.target === e.currentTarget) setShowPrestataireAddModal(false) }}>
          <div className="modal-box">
              <h3 className="mb-3">Ajouter un prestataire</h3>

              <label className="mt-2">Représentant légal</label>
              <input
                type="text"
                name="firstName"
                placeholder="Prénom"
                className="form-control mb-2"
                value={tempPrestataire.firstName}
                onChange={handleChangePrestataire}
              />
              <input
                type="text"
                name="lastName"
                placeholder="Nom"
                className="form-control mb-3"
                value={tempPrestataire.lastName}
                onChange={handleChangePrestataire}
              />

                <label className="mt-2">Entreprise / Société / Organisation</label>
                <input
                  type="text"
                  name="entrepriseName"
                  placeholder="Entreprise"
                  className="form-control mb-3"
                  value={tempPrestataire.entrepriseName}
                  onChange={handleChangePrestataire}
                />

                <label className="mt-2">Domicile</label>
                <input
                  type="text"
                  name="adresse"
                  placeholder="Adresse"
                  className="form-control mb-3"
                  value={tempPrestataire.adresse}
                  onChange={handleChangePrestataire}
                />

                <label className="mt-2">Contact</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  className="form-control mb-2"
                  value={tempPrestataire.email}
                  onChange={handleChangePrestataire}
                />
                <input
                  type="tel"
                  name="telephone"
                  placeholder="Téléphone"
                  className="form-control mb-3"
                  value={tempPrestataire.telephone}
                  onChange={handleChangePrestataire}
              />

            <div className="text-end mt-3">
              <button className="btn btn-secondary me-2" onClick={() => setShowPrestataireAddModal(false)}>Annuler</button>
              <button
                className="btn btn-success"
                onClick={handleAddPrestataire}
                disabled={
                  !tempPrestataire.firstName.trim() ||
                  !tempPrestataire.lastName.trim() ||
                  !tempPrestataire.entrepriseName.trim() ||
                  !tempPrestataire.adresse.trim() ||
                  !tempPrestataire.email.trim() ||
                  !tempPrestataire.telephone.trim()
                }
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Détails */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={e => closeOnOverlay(e, setShowDetailsModal)}>
          <div className="modal-box">
            <h3>Détails de l'offre</h3>
            <p>
              <strong>Statut:</strong>{" "}
              <span className={`status ${statusClassMap[tempAO.statut] || ""}`}>
                {displayStatus(tempAO.statut)}
              </span>
            </p>
            <p><strong>Référence :</strong> {tempAO.reference}</p>
            <p><strong>Titre:</strong> {tempAO.titre}</p>
            <p><strong>Description:</strong> {tempAO.description}</p>
          <p><strong>Exigences :</strong></p>
          {tempAO.exigence && tempAO.exigence.length > 0 ? (
            <ul>
              {tempAO.exigence.map((exigence, index) => (
                <li key={index}>
                  {exigence.replace(/[\[\]"]/g, '')}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">Aucune exigence renseignée.</p>
          )}

            <p><strong>Date Clôture:</strong> {tempAO.dateCloturation}</p>
            {selectedWinner ? (
              <div className="alert alert-success mt-3">
                <h5>Attribué à :</h5>
                <p><strong>{selectedWinner.firstName} {selectedWinner.lastName}</strong></p>
                <p>Entreprise : <strong>{selectedWinner.entrepriseName}</strong></p>
              </div>
            ) : (
              <div className="alert alert-secondary mt-3">
                Aucun prestataire attribué pour cet appel d'offre.
              </div>
            )}
            <div className="text-end mt-3">
              {tempAO.statut !== "Non publié" && tempAO.statut !== "rejeter" && !tempAO.isPublished && (
                <button className="btn btn-success" onClick={handlePublishOffer}>
                  Publier l’appel d'offre
                </button>
              )}
              {tempAO.statut === "resultat_de_marche" && (
                <>
                  {!showProjectInput && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowProjectInput(true)}
                    >
                      Basculer en projet
                    </button>
                  )}

                  {showProjectInput && (
                    <div
                      className="modal-overlay"
                      onClick={e => { if (e.target === e.currentTarget) setShowProjectInput(false); }}
                    >
                      <div className="modal-box">
                        <form
                          onSubmit={e => {
                            e.preventDefault();
                            if (!tempAO.idAo) return;

                            const newProject: Project = {
                              idAo: tempAO.idAo!,
                              startDate: projectStartDate,
                              endDate: projectEndDate,
                              lots: [],
                            };

                            createNewProject(newProject);
                            setShowProjectInput(false);
                          }}
                        >
                          <div className="mb-2">
                            <label className="fw-bold d-block text-start">Date de début</label>
                            <input
                              type="datetime-local"
                              className="form-control"
                              value={projectStartDate}
                              onChange={e => setProjectStartDate(e.target.value)}
                              required
                            />
                          </div>

                          <div className="mb-2">
                            <label className="fw-bold d-block text-start">Date de fin</label>
                            <input
                              type="datetime-local"
                              className="form-control"
                              value={projectEndDate}
                              onChange={e => setProjectEndDate(e.target.value)}
                              required
                            />
                          </div>

                          <div className="text-end mt-3">
                            <button
                              type="button"
                              className="btn btn-secondary me-2"
                              onClick={() => setShowProjectInput(false)}
                            >
                              Annuler
                            </button>
                            <button type="submit" className="btn btn-success">
                              Créer le projet
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </AuthentifiedPage>
  );
};

export default GestionOffre;